import { useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Section } from "../../components/Section.tsx";
import { useDataset } from "../../hooks/useDataset.ts";
import { useMutate } from "../../hooks/useMutate.ts";
import { addBudgetFolder, budgetSiblings, placeBudgetItem, placeRecurringUnderPost, removeBudgetFolder, renameBudgetFolder, type BudgetItemKey } from "../../../store/actions.ts";

export function BudgetFoldersSection() {
  const dataset = useDataset();
  const { mutate, error } = useMutate();
  const [name, setName] = useState("");
  const [parent, setParent] = useState<string | null>(null);

  const [folderNames, setFolderNames] = useState<Record<string, string>>({});
  const savingNames = useRef(new Set<string>());

  function saveFolderName(id: string, savedName: string) {
    const typed = folderNames[id];
    if (typed === undefined || typed === savedName || savingNames.current.has(id)) return;
    savingNames.current.add(id);
    void mutate(draft => renameBudgetFolder(draft, id, typed)).then(saved => {
      savingNames.current.delete(id);
      if (saved) setFolderNames(names => {
        if (names[id] !== typed) return names;
        const next = { ...names };
        delete next[id];
        return next;
      });
    });
  }

  function folderPath(id: string): string {
    const folder = dataset.folders.find(folder => folder.id === id)!;
    return folder.parentId === null ? folder.name : `${folderPath(folder.parentId)} / ${folder.name}`;
  }
  const folders = [...dataset.folders].sort((a, b) => folderPath(a.id).localeCompare(folderPath(b.id)));
  function allowedDestination(key: BudgetItemKey, id: string): boolean {
    if (key.kind !== "folder") return true;
    let cursor: string | null = id;
    while (cursor !== null) {
      if (cursor === key.id) return false;
      cursor = dataset.folders.find(folder => folder.id === cursor)!.parentId;
    }
    return true;
  }
  function children(parentId: string | null, parentPostId?: string): ReactNode {
    const siblings = parentPostId ? dataset.recurring.filter(cost => cost.parentPostId === parentPostId).sort((a, b) => a.order - b.order).map(cost => ({ kind: "recurring" as const, id: cost.id, order: cost.order })) : budgetSiblings(dataset, parentId);
    return <ul className="space-y-2">{siblings.map((key, index) => {
      const item = key.kind === "folder" ? dataset.folders.find(item => item.id === key.id)! : key.kind === "post" ? dataset.posts.find(item => item.id === key.id)! : key.kind === "purchase" ? dataset.purchases.find(item => item.id === key.id)! : dataset.recurring.find(item => item.id === key.id)!;
      const name = "description" in item ? item.description : item.name;
      return <li key={`${key.kind}-${key.id}`}>
        <div data-budget-item={key.id} className="flex flex-wrap items-center gap-2 border-b border-budget-rule py-2">
          <div className="min-w-0 flex-1 basis-40">
            {key.kind === "folder" ? <Input className="h-8 w-full max-w-64" aria-label={`Name for ${name}`} value={folderNames[key.id] ?? name} onInput={event => {
              const typed = event.currentTarget.value;
              setFolderNames(names => ({ ...names, [key.id]: typed }));
            }} onBlur={() => saveFolderName(key.id, name)} onKeyDown={event => {
              if (event.key === "Enter") {
                event.preventDefault();
                saveFolderName(key.id, name);
              }
            }} /> : <span className="break-words text-sm">{name}</span>}
            <span className="mt-1 block text-xs text-budget-ink-muted">{key.kind === "folder" ? "Folder" : key.kind === "post" ? "Post" : key.kind === "purchase" ? "Purchase" : "Bill"}</span>
          </div>
          <NativeSelect className="h-8 w-full sm:w-56 text-xs md:text-xs" aria-label={`Folder for ${name}`} value={parentPostId ? `post:${parentPostId}` : parentId ?? ""} onChange={event => {
            const destination = event.target.value || null;
            void mutate(draft => {
              if (key.kind === "recurring" && destination?.startsWith("post:")) placeRecurringUnderPost(draft, key.id, destination.slice(5));
              else placeBudgetItem(draft, key, destination, budgetSiblings(draft, destination).filter(sibling => !(sibling.kind === key.kind && sibling.id === key.id)).length);
            });
          }}>
            <option value="">Unfiled</option>
            {folders.filter(folder => allowedDestination(key, folder.id)).map(folder => <option key={folder.id} value={folder.id}>{folderPath(folder.id)}</option>)}
            {key.kind === "recurring" && dataset.posts.map(post => <option key={post.id} value={`post:${post.id}`}>Under {post.name}</option>)}
          </NativeSelect>
          <div className="flex gap-1">
            <Button size="xs" variant="ghost" aria-label={`Move ${name} up`} disabled={index === 0 || !!parentPostId} onClick={() => void mutate(draft => {
              const position = budgetSiblings(draft, parentId).findIndex(sibling => sibling.kind === key.kind && sibling.id === key.id);
              placeBudgetItem(draft, key, parentId, position - 1);
            })}>up</Button>
            <Button size="xs" variant="ghost" aria-label={`Move ${name} down`} disabled={index === siblings.length - 1 || !!parentPostId} onClick={() => void mutate(draft => {
              const position = budgetSiblings(draft, parentId).findIndex(sibling => sibling.kind === key.kind && sibling.id === key.id);
              placeBudgetItem(draft, key, parentId, position + 1);
            })}>down</Button>
            {key.kind === "folder" && <Button size="xs" variant="ghost" aria-label={`Remove folder ${name}`} title="Move contents to parent and remove folder" onClick={() => void mutate(draft => removeBudgetFolder(draft, key.id))}>remove</Button>}
          </div>
        </div>
        {key.kind === "folder" && <div className="ml-3 border-l border-budget-rule pl-3 sm:ml-5 sm:pl-5">{children(key.id)}</div>}
        {key.kind === "post" && dataset.recurring.some(cost => cost.parentPostId === key.id) && <div className="ml-3 border-l border-budget-rule pl-3 sm:ml-5 sm:pl-5">{children(parentId, key.id)}</div>}
      </li>;
    })}</ul>;
  }

  return <div id="budget-folders" className="scroll-mt-5"><Section title="Budget folders" hint="Organize folders, posts, bills, and placed purchases together. Moving a bill or purchase leaves its post charges unchanged.">
    {error && <p role="alert" className="mb-3 text-sm text-destructive">Could not save: {error}</p>}
    <h3 className="text-sm font-medium">Top level · Unfiled</h3>
    {children(null)}
    <div className="mt-5 flex flex-wrap items-end gap-2 border-t border-budget-rule pt-5">
      <label className="text-xs">Folder name<Input className="mt-1 w-48" aria-label="New folder name" value={name} onInput={event => setName(event.currentTarget.value)} /></label>
      <label className="text-xs">Parent folder<NativeSelect className="mt-1 w-56" aria-label="New folder parent" value={parent ?? ""} onChange={event => setParent(event.target.value || null)}>
        <option value="">Unfiled</option>{folders.map(folder => <option key={folder.id} value={folder.id}>{folderPath(folder.id)}</option>)}
      </NativeSelect></label>
      <Button variant="outline" disabled={!name.trim()} onClick={() => {
        const newName = name.trim(), destination = parent;
        void mutate(draft => { addBudgetFolder(draft, newName, destination); }).then(saved => { if (saved) setName(""); });
      }}>Add folder</Button>
    </div>
  </Section></div>;
}
